import { FC } from "react";
import { Autocomplete, Box, TextField } from "@mui/material";
import {
  COUNTRY_OPTIONS,
  CountryOption,
  findCountryByName
} from "../../utils/countries";
import { CountryFlagByCode } from "../countryFlag";

type Props = {
  value: string;
  onChange: (countryName: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
};

/**
 * The single way a country gets named anywhere in the app. Picking from a fixed list is the whole
 * point: it is what stops the same market arriving as "Denmark", "denmark", "Danmark" and "DK".
 *
 * Free text is deliberately not allowed. A request is always for a country, confirmed with Market
 * Access on 2026-09-08, so there is nothing the list cannot express.
 */
export const CountrySelect: FC<Props> = ({
  value,
  onChange,
  label = "Country",
  required,
  disabled,
  helperText
}) => (
  <Autocomplete
    options={COUNTRY_OPTIONS}
    value={findCountryByName(value)}
    disabled={disabled}
    autoHighlight
    getOptionLabel={(option: CountryOption) => option.name}
    isOptionEqualToValue={(option, selected) => option.code === selected.code}
    onChange={(_e, selected) => onChange(selected?.name ?? "")}
    renderOption={(props, option) => (
      // MUI puts the key on props; spreading it straight through keeps React happy and avoids
      // destructuring it into a variable ESLint reads as an undeclared prop.
      <Box
        component="li"
        {...props}
        key={option.code}
        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
      >
        <CountryFlagByCode code={option.code} />
        {option.name}
      </Box>
    )}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        required={required}
        helperText={helperText}
        slotProps={{
          input: {
            ...params.InputProps,
            startAdornment: (() => {
              const selected = findCountryByName(value);
              return selected ? (
                <Box sx={{ display: "flex", alignItems: "center", pl: 0.5 }}>
                  <CountryFlagByCode code={selected.code} />
                </Box>
              ) : null;
            })()
          }
        }}
      />
    )}
  />
);
